import React from "react";
import { Box, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { Edit as EditIcon, WarehouseOutlined as WarehouseIcon } from "@mui/icons-material";
import { LOT_STRATEGIES, WAREHOUSE_STRATEGIES, FALLBACK_OPTIONS } from "../constants";

function RulePill({ label, opt }) {
  const Icon = opt.icon;
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, background: "var(--bg-primary)", height: "100%" }}>
      <Typography
        fontSize={10}
        fontWeight={600}
        color="var(--text-secondary)"
        fontFamily="Poppins, sans-serif"
        mb={0.5}
        sx={{ textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: 1,
            background: opt.gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 13, color: "#fff" }} />
        </Box>
        <Typography fontSize={12} fontWeight={600} color="var(--text-tertiary)" fontFamily="Poppins, sans-serif">
          {opt.label}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ActiveRuleCard({ rule, onEdit }) {
  const lotOpt = LOT_STRATEGIES.find((o) => o.value === rule.lot_strategy);
  const whOpt = WAREHOUSE_STRATEGIES.find((o) => o.value === rule.warehouse_strategy);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1.5px solid",
        borderColor: rule.is_active ? "#38c17266" : "var(--border-color)",
        background: "var(--bg-paper)",
        p: 3,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {rule.is_active && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "linear-gradient(90deg, #43e97b, #38f9d7)",
          }}
        />
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography fontSize={16} fontWeight={700} color="var(--text-primary)" fontFamily="Poppins, sans-serif">
            {rule.name}
          </Typography>
          {rule.description && (
            <Typography fontSize={12} color="var(--text-secondary)" fontFamily="Poppins, sans-serif" mt={0.3}>
              {rule.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={rule.is_active ? "Active" : "Inactive"}
            size="small"
            color={rule.is_active ? "success" : "default"}
            sx={{ fontFamily: "Poppins, sans-serif", fontSize: 11 }}
          />
          <Tooltip title="Edit Rule">
            <IconButton
              size="small"
              onClick={() => onEdit(rule)}
              sx={{
                background: "var(--hover-bg)",
                "&:hover": { background: "var(--hover-bg)", opacity: 0.8 },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {lotOpt && (
          <Grid item xs={12} sm={6} md={3}>
            <RulePill label="Lot Strategy" opt={lotOpt} />
          </Grid>
        )}
        {whOpt && (
          <Grid item xs={12} sm={6} md={3}>
            <RulePill label="Warehouse Strategy" opt={whOpt} />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: "var(--bg-primary)", height: "100%" }}>
            <Typography
              fontSize={10}
              fontWeight={600}
              color="var(--text-secondary)"
              fontFamily="Poppins, sans-serif"
              mb={0.5}
              sx={{ textTransform: "uppercase" }}
            >
              Order Splitting
            </Typography>
            <Chip
              label={rule.split_order ? "Allowed" : "Not Allowed"}
              size="small"
              color={rule.split_order ? "primary" : "default"}
              sx={{ fontFamily: "Poppins, sans-serif", fontSize: 10 }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, background: "var(--bg-primary)", height: "100%" }}>
            <Typography
              fontSize={10}
              fontWeight={600}
              color="var(--text-secondary)"
              fontFamily="Poppins, sans-serif"
              mb={0.5}
              sx={{ textTransform: "uppercase" }}
            >
              Fallback
            </Typography>
            <Typography fontSize={12} fontWeight={600} color="var(--text-tertiary)" fontFamily="Poppins, sans-serif">
              {FALLBACK_OPTIONS.find((f) => f.value === rule.fallback_strategy)?.label || rule.fallback_strategy}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {rule.warehouse_strategy === "PRIORITY" && rule.warehouse_priority?.length > 0 && (
        <Box mt={2}>
          <Typography
            fontSize={11}
            fontWeight={600}
            color="var(--text-secondary)"
            fontFamily="Poppins, sans-serif"
            mb={1}
            sx={{ textTransform: "uppercase" }}
          >
            Warehouse Priority Order
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {rule.warehouse_priority.map((item, i) => (
              <Chip
                key={item.warehouse}
                icon={<WarehouseIcon sx={{ fontSize: 14 }} />}
                label={`${i + 1}. ${item.warehouse?.name} (${item.warehouse?.warehouse_code})`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: "Poppins, sans-serif", fontSize: 11 }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
